import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	input,
	InputSignal,
	signal,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NavigationComponent, NavigationItem } from '../navigation/navigation.component';
import { MDHeaderComponent } from '@melodic-ui/components';
import { PageBannerComponent } from '../page-banner/page-banner.component';
import { WindowScrollService } from '@melodic-ui/services';
import { ChatStateService } from '@lb/state/chat-state.service';
import { ChatSlideoutComponent } from '../chat-slideout/chat-slideout.component';

const NAV_EXPANDED_KEY = 'lb-nav-expanded';
const SMALL_SCREEN_BREAKPOINT = 1024;

@Component({
	selector: 'lb-application-container',
	standalone: true,
	imports: [RouterModule, CommonModule, NavigationComponent, MDHeaderComponent, PageBannerComponent, ChatSlideoutComponent],
	templateUrl: './application-container.component.html',
	styleUrl: './application-container.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationContainerComponent {
	private _elementRef: ElementRef = inject(ElementRef);
	private _windowScrollService: WindowScrollService = inject(WindowScrollService);
	private _chatStateService = inject(ChatStateService);
	private _destroyRef = inject(DestroyRef);

	public navigationItems: InputSignal<NavigationItem[]> = input<NavigationItem[]>([]);
	public navDisabled: InputSignal<boolean> = input<boolean>(false);
	public stickyOpt: WritableSignal<boolean> = signal(false);
	public chatOpen = this._chatStateService.isSlideoutOpen;
	public navExpanded: WritableSignal<boolean> = signal(false);

	constructor() {
		const stored = sessionStorage.getItem(NAV_EXPANDED_KEY);
		this.navExpanded.set(stored !== null ? stored === 'true' : window.innerWidth >= SMALL_SCREEN_BREAKPOINT);

		effect(() => {
			if (this.navDisabled()) {
				this.navExpanded.set(false);
			}
		});

		afterNextRender(() => {
			const scrollContainer = this._elementRef.nativeElement.querySelector('.page-content') as HTMLElement;
			const pageBanner = this._elementRef.nativeElement.querySelector('.page-banner') as HTMLElement;
			if (!scrollContainer) {
				return;
			}

			let bannerHeight = pageBanner?.clientHeight ?? 0;

			if (pageBanner) {
				const resizeObserver = new ResizeObserver((entries) => {
					bannerHeight = entries[0].contentRect.height;
				});
				resizeObserver.observe(pageBanner);
				this._destroyRef.onDestroy(() => resizeObserver.disconnect());
			}

			this._windowScrollService
				.getScrollOfTargetContainer(scrollContainer)
				.pipe(takeUntilDestroyed(this._destroyRef))
				.subscribe((scrollPosition) => {
					this.stickyOpt.set(scrollPosition.y > bannerHeight);
				});
		});
	}

	public toggleNav(): void {
		if (this.navDisabled()) return;
		const newState = !this.navExpanded();
		this.navExpanded.set(newState);
		sessionStorage.setItem(NAV_EXPANDED_KEY, String(newState));
	}
}
